import { Autocomplete, Button, OutlinedInput, Stack } from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps, ModalContext } from "..";
import UserGroupChip from "../../Misc/Chips/UserGroupChip/user-group-chip";
import { userService } from "../../../Core/Api/services";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useSelector } from "react-redux";
import { GetUsersList } from "../../../Redux/Reducers/redux-slice-settings-users";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import ModalAddGroup, { ModalAddGroupElementProps } from "../ModalAddGroup/modal-add-group";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalUsersChangeGroupElementProps = {
  users: number[];
  onClose?: (props: CloseClbProps) => void;
};
export type ModalUsersChangeGroupProps = CommonModalProps<ModalUsersChangeGroupElementProps>;

const ModalUsersChangeGroup = React.forwardRef<any, ModalUsersChangeGroupProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, ...modalProps } = props;
  const [groupList, setGroupList] = React.useState<GroupAdvancedInfos[]>([]);
  const [roleList, setRoleList] = React.useState<DbUserGroupRoleInfos[]>([]);
  const [groupSelected, setGroupSelected] = React.useState<GroupAdvancedInfos>();

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const usersList = useSelector(GetUsersList);

  const handleOpenModalNewGroup = React.useCallback(() => {
    if (!openModal) return;
    const onClose: ModalAddGroupElementProps["onClose"] = (data) => {};

    openModal<ModalAddGroupElementProps>(ModalAddGroup, {
      onClose,
    });
  }, [openModal]);

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = () => {
    if (groupSelected?.id) {
      userService.UpdateUsersGroup(
        usersList.filter((x) => props.users.includes(x.userInfos?.id!)),
        groupSelected?.id
      );
      onClose && onClose({ confirm: true });
      closeModal();
    }
  };

  React.useEffect(() => {
    userService.GetGroupsList().then(function (response) {
      response.groups =
        response.groups?.filter(
          (x) => x.group?.enabled && x.group.name !== "deleted" && x.group.name !== "Deleted Account"
        ) || [];
      if (response.success && response.groups) {
        setGroupList(response.groups);
      }
    });

    userService.GetGroupRolesList().then(function (response) {
      if (response.success) {
        setRoleList(response.groupRoles || []);
      }
    });
  }, []);

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.changeUsersGroup")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      height={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmClick}
            disabled={groupSelected === undefined}
          >
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {groupList && (
            <Autocomplete
              disablePortal
              options={groupList}
              sx={{ flex: 1 }}
              popupIcon={<ExpandMoreRoundedIcon />}
              getOptionLabel={(option) => option.group?.name || ""}
              onChange={(e, value) => {
                setGroupSelected(value ? value : undefined);
              }}
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  placeholder={t("field:placeholder.searchExistingGroup")}
                  sx={{ width: "100%" }}
                />
              )}
            />
          )}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenModalNewGroup}
            disabled={!UserHasAcl(AclTypes.aclGroupCreate)}
          >
            {t("common:newGroup")}
          </Button>
        </Stack>

        {groupSelected && (
          <Stack direction="row" flexWrap="wrap">
            <UserGroupChip roleId={groupSelected.group?.roleId} roles={roleList} sx={{ mr: 1, mb: 1 }} />
          </Stack>
        )}
      </Stack>
    </Modal>
  );
});

export default ModalUsersChangeGroup;
